import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes,
    faSearch,
    faFilter,
    faChevronLeft,
    faChevronRight,
    faFileAlt,
    faCalendar,
    faUsers,
    faNewspaper,
    faBook,
    faSpinner,
    faEye,
    faCopy
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import apiHelper from '../../utils/apiHelper';
import './CollectionDocumentsViewer.css';

const CollectionDocumentsViewer = ({ isOpen, onClose, collection }) => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [limit] = useState(50);

    useEffect(() => {
        if (isOpen && collection) {     
            fetchDocuments();
        }
    }, [isOpen, collection, currentPage, searchQuery, typeFilter]);

    const fetchDocuments = async () => {
        if (!collection) {
           
            return;
        }
        
       
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: limit,
                search: searchQuery,
                type: typeFilter
            };

           
            const response = await apiHelper.get(`/api/qdrant/collections/${collection.name}/documents`, { params });
            
               
            
            if (response.data.success) {
                setDocuments(response.data.documents);
                setTotalPages(response.data.pagination.totalPages);
                setTotalCount(response.data.pagination.totalCount);

            } else {
                console.error('API returned success=false:', response.data);
                toast.error(`Failed to load documents: ${response.data.error || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                data: error.response?.data
            });
            toast.error(`Error loading documents: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchDocuments();
    };

    const handleTypeFilter = (type) => {
        setTypeFilter(type);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'News': return faNewspaper;
            case 'Repository': return faBook;
            case 'Event': return faCalendar;
            case 'Working Group': return faUsers;
            case 'Submitted Article': return faNewspaper; // Using newspaper icon for submitted articles
            default: return faFileAlt;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'News': return '#ffc107';
            case 'Repository': return '#17a2b8';
            case 'Event': return '#28a745';
            case 'Working Group': return '#6c757d';
            case 'Submitted Article': return '#8b5cf6'; // Purple for submitted articles
            default: return '#dc3545';
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString();
        } catch {
            return dateStr;
        }
    };

    if (!isOpen) return null;

    return (
        <div className="collection-documents-modal">
            <div className="modal-backdrop" onClick={onClose}></div>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>
                        <FontAwesomeIcon icon={faEye} className="header-icon" />
                        Collection Documents
                    </h2>
                    <button onClick={onClose} className="close-btn">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="collection-info">
                        <h3>{collection?.name}</h3>
                        <p>Total Documents: {totalCount.toLocaleString()}</p>
                       
                    </div>

                    <div className="controls">
                        <form onSubmit={handleSearch} className="search-form">
                            <div className="search-input-group">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search documents..."
                                    className="search-input"
                                />
                                <button type="submit" className="search-btn">Search</button>
                            </div>
                        </form>

                        <div className="filter-controls">
                            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                            <button
                                className={`filter-btn ${typeFilter === '' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('')}
                            >
                                All
                            </button>
                            <button
                                className={`filter-btn ${typeFilter === 'News' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('News')}
                            >
                                📰 News
                            </button>
                            <button
                                className={`filter-btn ${typeFilter === 'Repository' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('Repository')}
                            >
                                📚 Repository
                            </button>
                            <button
                                className={`filter-btn ${typeFilter === 'Event' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('Event')}
                            >
                                📅 Events
                            </button>
                            <button
                                className={`filter-btn ${typeFilter === 'Working Group' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('Working Group')}
                            >
                                👥 Working Groups
                            </button>
                            <button
                                className={`filter-btn ${typeFilter === 'Submitted Article' ? 'active' : ''}`}
                                onClick={() => handleTypeFilter('Submitted Article')}
                            >
                                ✍️ Submitted Articles
                            </button>
                        </div>
                    </div>

                    <div className="documents-list">
                        {loading ? (
                            <div className="loading">
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>Loading documents...</span>
                            </div>
                        ) : documents.length === 0 ? (
                            <div className="empty-state">
                                <FontAwesomeIcon icon={faFileAlt} />
                                <p>No documents found</p>
                            </div>
                        ) : (
                            documents.map(doc => (
                                <div key={doc.id} className="document-item">
                                    <div className="document-header">
                                        <div className="document-title">
                                            <FontAwesomeIcon
                                                icon={getTypeIcon(doc.type)}
                                                style={{ color: getTypeColor(doc.type) }}
                                                className="type-icon"
                                            />
                                            <span className="title">{doc.title}</span>
                                        </div>
                                        <div className="document-actions">
                                            <button
                                                onClick={() => copyToClipboard(doc.title)}
                                                className="copy-btn"
                                                title="Copy title"
                                            >
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="document-meta">
                                        <span className="type-badge" style={{ backgroundColor: getTypeColor(doc.type) }}>
                                            {doc.type}
                                        </span>
                                        {doc.workingGroup && (
                                            <span className="meta-item">
                                                <strong>Working Group:</strong> {doc.workingGroup}
                                            </span>
                                        )}
                                        {doc.year && (
                                            <span className="meta-item">
                                                <strong>Year:</strong> {doc.year}
                                            </span>
                                        )}
                                        {doc.publishDate && (
                                            <span className="meta-item">
                                                <strong>Published:</strong> {formatDate(doc.publishDate)}
                                            </span>
                                        )}
                                        {doc.eventDate && (
                                            <span className="meta-item">
                                                <strong>Event Date:</strong> {formatDate(doc.eventDate)}
                                            </span>
                                        )}
                                        {doc.fileName && (
                                            <span className="meta-item">
                                                <strong>File:</strong> {doc.fileName}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {doc.summary && (
                                        <div className="document-summary">
                                            <strong>Summary:</strong> {doc.summary}
                                        </div>
                                    )}
                                    
                                    {doc.content_preview && (
                                        <div className="document-preview">
                                            <strong>Preview:</strong> {doc.content_preview}...
                                        </div>
                                    )}
                                    
                                    <div className="document-id">
                                        <small>ID: {doc.id}</small>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="pagination-btn"
                            >
                                <FontAwesomeIcon icon={faChevronLeft} />
                                Previous
                            </button>
                            
                            <span className="pagination-info">
                                Page {currentPage} of {totalPages} ({totalCount.toLocaleString()} total)
                            </span>
                            
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="pagination-btn"
                            >
                                Next
                                <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CollectionDocumentsViewer; 